import React, {useContext, useEffect, useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, FormControlLabel, Grid, IconButton, Radio, Stack} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import {EditRounded} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {useForm, FormProvider} from "react-hook-form";
import {AxiosError} from "axios";
import {financialInfoState} from "../../../search-dms/state";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {updateFinancialInfo} from "../../../search-dms/dmsSearch.actions";
import {SET_SNACKBAR} from "../../../common/utils";
import FormTextField from "../../../common/form-fields-wrappers/FormTextField";
import { FormRadioGroup } from "../../../common/form-fields-wrappers";
import {useHasScope} from "../../../../../config/globalConfig";
import {NetworkScope} from "../../../types";

const FinancialInfo = () => {
    const hasScope = useHasScope;
    const {t} = useTranslation(['network']);
    const [financialInfo, setFinancialInfo] = useRecoilState(financialInfoState);
    const { dispatchGlobalData } = useContext(GlobalContext);
    const financialInfoForm = useForm({
        defaultValues: {
            feesDued: financialInfo?.feesDued === true ?  "Yes" : "No",
            royaltiesDued: financialInfo?.royaltiesDued === true ? "Yes" : "No",
            representativeIdentificationCode: financialInfo?.representativeIdentificationCode ?? "",
            representativeAccountCode: financialInfo?.representativeAccountCode ?? "",
            clientIdentificationCode: financialInfo?.clientIdentificationCode ?? "",
            clientAccountCode: financialInfo?.clientAccountCode ?? "",
            acgIdentificationCode: financialInfo?.acgIdentificationCode ?? "",
            perceiver: financialInfo?.perceiver ?? "",
            perceiverAddress: financialInfo?.perceiverAddress ?? "",
            perceiverCity: financialInfo?.perceiverCity ?? "",
            perceiverProvince: financialInfo?.perceiverProvince ?? "",
            perceiverPostalCode: financialInfo?.perceiverPostalCode ?? ""
        }
    });
    const onSubmit = (data: any) => {
        const reqParameters = {
            id: financialInfo?.id,
            feesDued: data.feesDued === "Yes",
            royaltiesDued: data.royaltiesDued === "Yes",
            representativeIdentificationCode: data.representativeIdentificationCode,
            representativeAccountCode: data.representativeAccountCode,
            clientIdentificationCode: data.clientIdentificationCode,
            clientAccountCode: data.clientAccountCode,
            acgIdentificationCode: data.acgIdentificationCode,
            perceiver: data.perceiver,
            perceiverAddress: data.perceiverAddress,
            perceiverCity: data.perceiverCity,
            perceiverProvince: data.perceiverProvince,
            perceiverPostalCode: data.perceiverPostalCode
        }
        setOpen(false);
        setIsDisabled(true);
        setFinancialInfo(financialInfo);
        updateFinancialInfo(reqParameters)
            .then( (res:any) => {
                setFinancialInfo(res.financialInfo);
            })
            .catch((err: AxiosError) => {
                dispatchGlobalData(SET_SNACKBAR.ERROR);
            });

    }
    const [isDisabled, setIsDisabled] = useState(true);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const [open, setOpen] = useState(false);
    const handleDisableForm = (value:boolean) => {
        setIsDisabled(value);
    }

    useEffect(() => {
       financialInfoForm.setValue("feesDued",financialInfo?.feesDued === true ?  "Yes" : "No");
       financialInfoForm.setValue("royaltiesDued",financialInfo?.royaltiesDued === true ? "Yes" : "No");
       financialInfoForm.setValue("representativeIdentificationCode",financialInfo?.representativeIdentificationCode);
       financialInfoForm.setValue("representativeAccountCode",financialInfo?.representativeAccountCode);
       financialInfoForm.setValue("clientIdentificationCode",financialInfo?.clientIdentificationCode);
       financialInfoForm.setValue("clientAccountCode",financialInfo?.clientAccountCode);
       financialInfoForm.setValue("acgIdentificationCode",financialInfo?.acgIdentificationCode);
       financialInfoForm.setValue("perceiver",financialInfo?.perceiver);
       financialInfoForm.setValue("perceiverAddress",financialInfo?.perceiverAddress);
       financialInfoForm.setValue("perceiverCity",financialInfo?.perceiverCity);
       financialInfoForm.setValue("perceiverProvince",financialInfo?.perceiverProvince);
       financialInfoForm.setValue("perceiverPostalCode",financialInfo?.perceiverPostalCode);
    },[financialInfo])

    return(
        <>
            <FormProvider {...financialInfoForm}>
                <Grid container direction="row" alignItems="start">
                    <Grid item xs={12}>
                        <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                            {hasScope(NetworkScope.MsMng) && isDisabled &&
                                <IconButton color={"accent"} onClick={() => {handleDisableForm(false)}}>
                                    <EditRounded/>
                                </IconButton>
                            }
                        </Stack>
                    </Grid>
                    <Grid container item direction="row" xs={12} columnSpacing={2}>
                        <Grid item xs={12} sm={3}>
                            <FormRadioGroup
                                control={financialInfoForm.control}
                                name="feesDued"
                                label={t("dmsDetail.financialInfo.form.label.feesDued")}>
                                <FormControlLabel
                                    disabled={isDisabled}
                                    value="Yes"
                                    control={<Radio/>}
                                    label={t("dmsDetail.financialInfo.form.label.yes")} />
                                <FormControlLabel
                                    disabled={isDisabled}
                                    value="No"
                                    control={<Radio />}
                                    label={t("dmsDetail.financialInfo.form.label.no")}
                                />
                            </FormRadioGroup>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <FormRadioGroup
                                control={financialInfoForm.control}
                                name="royaltiesDued"
                                label={t("dmsDetail.financialInfo.form.label.royaltiesDued")}>
                                <FormControlLabel
                                    disabled={isDisabled}
                                    value="Yes"
                                    control={<Radio/>}
                                    label={t("dmsDetail.financialInfo.form.label.yes")} />
                                <FormControlLabel
                                    disabled={isDisabled}
                                    value="No"
                                    control={<Radio />}
                                    label={t("dmsDetail.financialInfo.form.label.no")}
                                />
                            </FormRadioGroup>
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={financialInfoForm.control}
                                name="representativeIdentificationCode"
                                label={t("dmsDetail.financialInfo.form.label.representativeIdentificationCode")}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={financialInfoForm.control}
                                name="representativeAccountCode"
                                label={t("dmsDetail.financialInfo.form.label.representativeAccountCode")}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={financialInfoForm.control}
                                name="clientIdentificationCode"
                                label={t("dmsDetail.financialInfo.form.label.clientIdentificationCode")}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={financialInfoForm.control}
                                name="clientAccountCode"
                                label={t("dmsDetail.financialInfo.form.label.clientAccountCode")}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={financialInfoForm.control}
                                name="acgIdentificationCode"
                                label={t("dmsDetail.financialInfo.form.label.acgIdentificationCode")}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={financialInfoForm.control}
                                name="perceiver"
                                label={t("dmsDetail.financialInfo.form.label.perceiver")}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={financialInfoForm.control}
                                name="perceiverAddress"
                                label={t("dmsDetail.financialInfo.form.label.perceiverAddress")}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={financialInfoForm.control}
                                name="perceiverCity"
                                label={t("dmsDetail.financialInfo.form.label.perceiverCity")}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={financialInfoForm.control}
                                name="perceiverProvince"
                                label={t("dmsDetail.financialInfo.form.label.perceiverProvince")}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={financialInfoForm.control}
                                name="perceiverPostalCode"
                                label={t("dmsDetail.financialInfo.form.label.perceiverPostalCode")}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                                { !isDisabled &&
                                    <Button variant="contained" onClick={handleOpen}>
                                        {t("dmsDetail.financialInfo.button.edit")}
                                    </Button>
                                }
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>
            </FormProvider>

            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {t("dmsDetail.generalInfo.dialog.title")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t("dmsDetail.generalInfo.dialog.content")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant={"outlined"} color={"accent"}>
                        {t("dmsDetail.generalInfo.dialog.action.cancel")}
                    </Button>
                    <Button onClick={financialInfoForm.handleSubmit(onSubmit)} autoFocus>
                        {t("dmsDetail.generalInfo.dialog.action.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default FinancialInfo;